import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const asList = (value) => (Array.isArray(value) ? value : []);

export const CalendarSettingsForms = ({ settings, onChange }) => {
  const periods = asList(settings.periods);
  const terms = asList(settings.terms);
  const holidays = asList(settings.holidays);

  const update = (key, value) => onChange({ ...settings, [key]: value });
  const changeRow = (key, index, field, value) => {
    const next = asList(settings[key]).map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: value } : row,
    );
    update(key, next);
  };
  const removeRow = (key, index) => {
    update(
      key,
      asList(settings[key]).filter((_, rowIndex) => rowIndex !== index),
    );
  };
  const addPeriod = () => {
    const highestPeriod = periods.reduce(
      (highest, row) => Math.max(highest, Number(row.period) || 0),
      0,
    );
    update("periods", [
      ...periods,
      { period: highestPeriod + 1, start: "09:00", end: "10:00" },
    ]);
  };

  return (
    <div className="space-y-7" data-testid="calendar-settings-forms">
      <section className="space-y-3" data-testid="settings-timetable-cycle">
        <div>
          <h3 className="font-semibold">Timetable cycle</h3>
          <p className="text-sm text-muted-foreground">
            Use a single weekly timetable, or alternate Week A and Week B.
          </p>
        </div>
        <div className="max-w-sm space-y-1">
          <Label htmlFor="settings-timetable-cycle-select">Timetable type</Label>
          <Select
            value={String(settings.timetable_weeks || 2)}
            onValueChange={(value) => update("timetable_weeks", Number(value))}
          >
            <SelectTrigger
              id="settings-timetable-cycle-select"
              data-testid="settings-timetable-cycle-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Single week timetable</SelectItem>
              <SelectItem value="2">Two week timetable (Week A and Week B)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>
      <section className="space-y-3" data-testid="settings-periods">
        <div>
          <h3 className="font-semibold">Periods and bell times</h3>
          <p className="text-sm text-muted-foreground">
            Set the start and end time for each teaching period.
          </p>
        </div>
        <div className="rounded-lg border" role="table" data-testid="settings-periods-table">
          <div
            className="hidden grid-cols-[minmax(8rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_3rem] gap-3
              border-b bg-muted/40 px-3 py-2 sm:grid"
            role="row"
          >
            <span className="text-xs font-medium text-muted-foreground" role="columnheader">
              Period number
            </span>
            <span className="text-xs font-medium text-muted-foreground" role="columnheader">
              Start time
            </span>
            <span className="text-xs font-medium text-muted-foreground" role="columnheader">
              End time
            </span>
            <span className="sr-only" role="columnheader">
              Remove period
            </span>
          </div>
          {periods.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground" data-testid="settings-periods-empty">
              No periods added yet.
            </p>
          ) : (
            periods.map((period, index) => (
              <div
                key={`period-${index}`}
                className="grid gap-3 border-b px-3 py-3 last:border-b-0 sm:grid-cols-[minmax(8rem,1fr)_minmax(9rem,1fr)_minmax(9rem,1fr)_3rem] sm:items-end"
                role="row"
                data-testid={`settings-period-row-${index}`}
              >
                <div className="space-y-1" role="cell">
                  <Label className="text-xs sm:sr-only" htmlFor={`settings-period-number-${index}`}>
                    Period number
                  </Label>
                  <Input
                    id={`settings-period-number-${index}`}
                    min="1"
                    type="number"
                    value={period.period ?? ""}
                    onChange={(event) =>
                      changeRow("periods", index, "period", event.target.value)
                    }
                    data-testid={`settings-period-number-${index}`}
                  />
                </div>
                <div className="space-y-1" role="cell">
                  <Label className="text-xs sm:sr-only" htmlFor={`settings-period-start-${index}`}>
                    Start time
                  </Label>
                  <Input
                    id={`settings-period-start-${index}`}
                    type="time"
                    value={period.start || ""}
                    onChange={(event) => changeRow("periods", index, "start", event.target.value)}
                    data-testid={`settings-period-start-${index}`}
                  />
                </div>
                <div className="space-y-1" role="cell">
                  <Label className="text-xs sm:sr-only" htmlFor={`settings-period-end-${index}`}>
                    End time
                  </Label>
                  <Input
                    id={`settings-period-end-${index}`}
                    type="time"
                    value={period.end || ""}
                    onChange={(event) => changeRow("periods", index, "end", event.target.value)}
                    data-testid={`settings-period-end-${index}`}
                  />
                </div>
                <Button
                  aria-label={`Delete period ${index + 1}`}
                  className="justify-self-start sm:justify-self-end"
                  onClick={() => removeRow("periods", index)}
                  size="icon"
                  title="Delete period"
                  type="button"
                  variant="ghost"
                  data-testid={`settings-period-delete-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
        <Button
          onClick={addPeriod}
          size="sm"
          type="button"
          variant="outline"
          data-testid="settings-add-period-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add period
        </Button>
      </section>

      <section className="space-y-3" data-testid="settings-terms">
        <div>
          <h3 className="font-semibold">Terms</h3>
          <p className="text-sm text-muted-foreground">
            Choose the timetable week that each term begins on.
          </p>
        </div>
        <div className="space-y-3">
          {terms.length === 0 ? (
            <p className="rounded-lg border px-3 py-4 text-sm text-muted-foreground" data-testid="settings-terms-empty">
              No terms added yet.
            </p>
          ) : (
            terms.map((term, index) => (
              <div
                key={`term-${index}`}
                className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-[1.25fr_1fr_1fr_9rem_3rem] lg:items-end"
                data-testid={`settings-term-row-${index}`}
              >
                <div className="space-y-1">
                  <Label htmlFor={`settings-term-name-${index}`}>Term name</Label>
                  <Input
                    id={`settings-term-name-${index}`}
                    value={term.name || ""}
                    onChange={(event) => changeRow("terms", index, "name", event.target.value)}
                    data-testid={`settings-term-name-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`settings-term-start-${index}`}>Start date</Label>
                  <Input
                    id={`settings-term-start-${index}`}
                    type="date"
                    value={term.start || ""}
                    onChange={(event) => changeRow("terms", index, "start", event.target.value)}
                    data-testid={`settings-term-start-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`settings-term-end-${index}`}>End date</Label>
                  <Input
                    id={`settings-term-end-${index}`}
                    type="date"
                    value={term.end || ""}
                    onChange={(event) => changeRow("terms", index, "end", event.target.value)}
                    data-testid={`settings-term-end-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`settings-term-week-${index}`}>Starts on</Label>
                  <Select
                    value={term.start_week || "A"}
                    onValueChange={(value) => changeRow("terms", index, "start_week", value)}
                  >
                    <SelectTrigger
                      id={`settings-term-week-${index}`}
                      data-testid={`settings-term-week-${index}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">Week A</SelectItem>
                      <SelectItem value="B">Week B</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  aria-label={`Delete term ${index + 1}`}
                  className="justify-self-start lg:justify-self-end"
                  onClick={() => removeRow("terms", index)}
                  size="icon"
                  title="Delete term"
                  type="button"
                  variant="ghost"
                  data-testid={`settings-term-delete-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
        <Button
          onClick={() =>
            update("terms", [
              ...terms,
              { name: "", start: "", end: "", start_week: "A" },
            ])
          }
          size="sm"
          type="button"
          variant="outline"
          data-testid="settings-add-term-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add term
        </Button>
      </section>

      <section className="space-y-3" data-testid="settings-holidays">
        <div>
          <h3 className="font-semibold">Holidays within terms</h3>
          <p className="text-sm text-muted-foreground">
            Add closure dates that should not count as school days.
          </p>
        </div>
        <div className="space-y-3">
          {holidays.length === 0 ? (
            <p className="rounded-lg border px-3 py-4 text-sm text-muted-foreground" data-testid="settings-holidays-empty">
              No holidays added yet.
            </p>
          ) : (
            holidays.map((holiday, index) => (
              <div
                key={`holiday-${index}`}
                className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2 lg:grid-cols-[1.25fr_1fr_1fr_3rem] lg:items-end"
                data-testid={`settings-holiday-row-${index}`}
              >
                <div className="space-y-1">
                  <Label htmlFor={`settings-holiday-name-${index}`}>Holiday name</Label>
                  <Input
                    id={`settings-holiday-name-${index}`}
                    value={holiday.name || ""}
                    onChange={(event) =>
                      changeRow("holidays", index, "name", event.target.value)
                    }
                    data-testid={`settings-holiday-name-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`settings-holiday-start-${index}`}>Start date</Label>
                  <Input
                    id={`settings-holiday-start-${index}`}
                    type="date"
                    value={holiday.start || ""}
                    onChange={(event) =>
                      changeRow("holidays", index, "start", event.target.value)
                    }
                    data-testid={`settings-holiday-start-${index}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`settings-holiday-end-${index}`}>End date</Label>
                  <Input
                    id={`settings-holiday-end-${index}`}
                    type="date"
                    value={holiday.end || ""}
                    onChange={(event) => changeRow("holidays", index, "end", event.target.value)}
                    data-testid={`settings-holiday-end-${index}`}
                  />
                </div>
                <Button
                  aria-label={`Delete holiday ${index + 1}`}
                  className="justify-self-start lg:justify-self-end"
                  onClick={() => removeRow("holidays", index)}
                  size="icon"
                  title="Delete holiday"
                  type="button"
                  variant="ghost"
                  data-testid={`settings-holiday-delete-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
        <Button
          onClick={() => update("holidays", [...holidays, { name: "", start: "", end: "" }])}
          size="sm"
          type="button"
          variant="outline"
          data-testid="settings-add-holiday-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add holiday
        </Button>
      </section>
    </div>
  );
};